/* venba-article-image-rescue.js (v8.4.39, May 6 2026)
 * ─────────────────────────────────────────────────────────────────────
 * Rescues broken product-photo images in legacy article content.
 *
 * Old Venba articles (e.g. 2021-era "Spook-tacular fragrances")
 * embed product photos via the legacy Shopify CDN scheme
 *   https://cdn.shopify.com/.../products/PRODUCT-NAME.jpg
 * Those URLs are dead — Shopify migrated product imagery to /files/
 * AND the original products are often discontinued/replaced. Direct
 * URL substitution returns 404 in nearly every case.
 *
 * This script:
 *   1. Detects broken <img> in #article-content (naturalWidth === 0)
 *   2. Derives a search query from the original filename
 *      (handles _720x size suffixes, UUID suffixes, hyphen/underscore
 *      separators, CamelCase product names, and ?v= cache params)
 *   3. Hits /search/suggest.json to find a current product match
 *   4. If found: swaps src to the current product image AND wraps the
 *      <img> in an <a> that links to the product page (replaces a dead
 *      visual with a live commerce moment — actually better than the
 *      original article's static product photo)
 *   5. If no match: hides the image and any orphaned wrapping <p>
 *
 * No theme rendering work to do — this is purely a runtime patch over
 * legacy content we can't edit (Arun has no Admin API access). Runs only
 * on /blogs/.../articles/... pages where #article-content is present.
 */
(function () {
  'use strict';

  function filenameToQuery(src) {
    var f = src.split('/').pop().split('?')[0];
    f = f.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');
    // Drop _NNNx Shopify size suffix
    f = f.replace(/_\d+x(?=$|[._-])/g, '');
    // Drop _UUID suffixes (8-4-4-4-12 hex pattern)
    f = f.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}.*$/i, '');
    // Drop trailing _N (e.g. _2 from a duplicate upload)
    f = f.replace(/_\d+$/, '');
    // Replace separators with space
    f = f.replace(/[-_]+/g, ' ');
    // Split CamelCase: "RougeSmoking" → "Rouge Smoking"
    f = f.replace(/([a-z])([A-Z])/g, '$1 $2');
    return f.toLowerCase().trim();
  }

  function searchProduct(query) {
    if (!query || query.length < 3) return Promise.resolve(null);
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
              '&resources[type]=product&resources[limit]=1';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var p = j && j.resources && j.resources.results &&
                j.resources.results.products && j.resources.results.products[0];
        if (!p) return null;
        return {
          title: p.title,
          handle: p.handle,
          url: p.url || ('/products/' + p.handle),
          image: p.image || p.featured_image
        };
      })
      .catch(function () { return null; });
  }

  function hideImage(img) {
    img.style.display = 'none';
    var parent = img.parentElement;
    // If the image was the only thing in a <p>, hide the <p> too
    if (parent && parent.tagName === 'P' && !parent.textContent.trim() &&
        parent.querySelectorAll('img:not([style*="display: none"])').length === 0) {
      parent.style.display = 'none';
    }
  }

  function rescueImage(img) {
    if (img.dataset.venbaRescueAttempted) return;
    img.dataset.venbaRescueAttempted = '1';

    var originalSrc = img.src;
    var query = filenameToQuery(originalSrc);

    searchProduct(query).then(function (match) {
      if (!match || !match.image) {
        hideImage(img);
        return;
      }

      // Swap image src
      img.src = match.image;
      img.alt = match.title;
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.classList.add('venba-rescued-img');
      img.loading = 'lazy';

      // Wrap in product link if not already
      var existingLink = img.closest('a');
      if (!existingLink || existingLink.getAttribute('href') !== match.url) {
        var a = document.createElement('a');
        a.href = match.url;
        a.className = 'venba-rescued-img-link';
        a.title = match.title;
        a.setAttribute('aria-label', 'Shop ' + match.title);
        img.parentNode.insertBefore(a, img);
        a.appendChild(img);
      }
    });
  }

  function checkAndRescue(img) {
    // Skip if image is already loaded successfully
    if (img.complete && img.naturalWidth > 0) return;
    // Skip non-product CDN images (e.g. site assets)
    if (!/cdn\.shopify\.com.*\/(products|files)\//.test(img.src)) return;

    if (img.complete && img.naturalWidth === 0) {
      rescueImage(img);
    } else {
      img.addEventListener('error', function () { rescueImage(img); }, { once: true });
      // Belt-and-suspenders: also check after a short delay
      setTimeout(function () {
        if (img.complete && img.naturalWidth === 0 && !img.dataset.venbaRescueAttempted) {
          rescueImage(img);
        }
      }, 1500);
    }
  }

  function init() {
    var root = document.getElementById('article-content');
    if (!root) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      checkAndRescue(imgs[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
